import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "./utils";

// Cache for the current page load. Re-fetches on every mount, every window
// focus, and every BroadcastChannel "cms:invalidate" message. This guarantees
// admin edits show up across tabs WITHOUT a hard refresh.
let cache = null;
let inflight = null;
let lastFetch = 0;
const STALE_MS = 8000; // re-fetch in background if older than 8s
const subscribers = new Set();

function notify() {
  subscribers.forEach((s) => s());
}

async function load(force = false) {
  const age = Date.now() - lastFetch;
  if (!force && cache && age < STALE_MS) return cache;
  if (inflight) return inflight;
  // cache-bust query so no CDN/browser stale response is returned
  inflight = axios
    .get(`${API}/content`, { params: { _ts: Date.now() } })
    .then((r) => {
      cache = r.data || {};
      lastFetch = Date.now();
      notify();
      return cache;
    })
    .catch(() => {
      cache = cache || {};
      return cache;
    })
    .finally(() => {
      inflight = null;
    });
  return inflight;
}

// Cross-tab invalidation channel
let bc = null;
try {
  if (typeof window !== "undefined" && "BroadcastChannel" in window) {
    bc = new BroadcastChannel("cms");
    bc.onmessage = (evt) => {
      if (evt.data === "invalidate") load(true);
    };
  }
} catch (_) { /* no-op */ }

// Window focus → re-fetch fresh content
if (typeof window !== "undefined") {
  window.addEventListener("focus", () => load(true));
  window.addEventListener("pageshow", () => load(true));
}

/**
 * useContent("home.mission.title", "Healing the world…")
 * Returns the admin-edited value if set, otherwise the fallback.
 * Re-fetches automatically on mount, window focus, cross-tab notify.
 */
export function useContent(key, fallback = "") {
  const [, force] = useState(0);

  useEffect(() => {
    const sub = () => force((n) => n + 1);
    subscribers.add(sub);
    // Always trigger a fresh load on mount
    load();
    return () => subscribers.delete(sub);
  }, []);

  if (cache && typeof cache[key] === "string" && cache[key].length > 0) {
    return cache[key];
  }
  return fallback;
}

/** Call after an admin save to instantly notify ALL tabs (including this one). */
export function refreshContent() {
  if (bc) {
    try { bc.postMessage("invalidate"); } catch (_) {}
  }
  return load(true);
}

/** Returns the whole CMS content map (live-updating). Use for dynamic key lists. */
export function useContentMap() {
  const [, force] = useState(0);
  useEffect(() => {
    const sub = () => force((n) => n + 1);
    subscribers.add(sub);
    load();
    return () => subscribers.delete(sub);
  }, []);
  return cache || {};
}
